import { computed, reactive } from 'vue'

import type { Ref } from 'vue'
import type Flickity from 'flickity'

type FocusState = {
  items: HTMLElement[],
  element?: HTMLElement,
  initialFocusItem?: HTMLElement,
  firstItem?: HTMLElement,
  lastItem?: HTMLElement,
  escapeCallback?: (() => void),
  tabCallback?: (() => void),
  tabbingEventListenersAdded: boolean
}

/**
 * Whether the user is currently navigating via keyboard (Tab key), as tracked by the
 * global `bx-js-user-is-tabbing` body class.
 */
export function isUserTabbing () {
  return document.body.classList.contains('bx-js-user-is-tabbing')
}

export function useKeepTabbingFocus (flickityRef?: Ref<Flickity | null>) {
  const focus = reactive<FocusState>({
    items: [],
    tabbingEventListenersAdded: false
  })

  const leaveFocusOnTab = computed(() => !!focus.tabCallback)
  const checkKeysFirstLast = computed(() => leaveFocusOnTab.value ? ['up', 'down'] : ['Tab'])

  /**
   * Activates focus trap inside a given element.
   * @param {HTMLElement} element - Container element to trap focus inside
   * @param {string} focusableElements - CSS selector for focusable elements (e.g. 'a, button, input')
   * @param {number} initialFocusIndex - Index of element to receive initial focus
   * @param {Function} escapeCallback - Called when Escape key is pressed
   */
  function keepTabbingFocus (
    element: HTMLElement,
    focusableElements: string[],
    initialFocusIndex = 0,
    escapeCallback?: () => void,
    tabCallback?: () => void
  ) {
    // Only activate focus trap if user is using keyboard
    if (!isUserTabbing()) return

    focus.escapeCallback = escapeCallback
    focus.tabCallback = tabCallback
    focus.element = element
    focus.items = []

    // Find all focusable elements inside the container
    const nodeList = Array.from(element.querySelectorAll<HTMLElement>(focusableElements.join(',')))
    focus.items.push(...nodeList)

    // Assign initial, first, and last elements
    focus.initialFocusItem = focus.items[initialFocusIndex] ?? focus.items[0]
    focus.firstItem = focus.items[0]
    focus.lastItem = focus.items[focus.items.length - 1] ?? null

    // Focus the initial element after a short delay
    setTimeout(() => {
      focus.initialFocusItem?.focus()
    }, 100)

    addTabbingEventListeners()
  }

  /**
   * Add event listeners to loop tabbing and handle Escape key
   */
  function addTabbingEventListeners () {
    focus.element!.addEventListener('keyup', eventKeyupEscape)
    if (leaveFocusOnTab.value) {
      focus.element!.addEventListener('keydown', eventKeydownTab)
      focus.element!.addEventListener('keydown', eventKeydownArrow)
    } else {
      focus.firstItem?.addEventListener('keydown', eventKeydownFirst)
      focus.lastItem?.addEventListener('keydown', eventKeydownLast)
    }
    focus.tabbingEventListenersAdded = true
  }

  /**
   * Remove event listeners for tabbing and Escape key
   */
  function removeTabbingEventListeners () {
    if (!focus.tabbingEventListenersAdded) return
    focus.element!.removeEventListener('keyup', eventKeyupEscape)
    if (leaveFocusOnTab.value) {
      focus.element!.removeEventListener('keydown', eventKeydownTab)
      focus.element!.removeEventListener('keydown', eventKeydownArrow)
    } else {
      focus.firstItem?.removeEventListener('keydown', eventKeydownFirst)
      focus.lastItem?.removeEventListener('keydown', eventKeydownLast)
    }
    focus.tabbingEventListenersAdded = false
  }

  /**
   * Handle Shift+Tab on the first focusable item
   * Loops focus to the last item
   */
  function eventKeydownFirst (e: KeyboardEvent) {
    if (checkKeysFirstLast.value.includes(e.key) && e.shiftKey) {
      e.preventDefault()
      focus.lastItem?.focus()
    }
  }

  /**
   * Handle Tab on the last focusable item
   * Loops focus back to the first item
   */
  function eventKeydownLast (e: KeyboardEvent) {
    if (checkKeysFirstLast.value.includes(e.key) && !e.shiftKey) {
      e.preventDefault()
      focus.firstItem?.focus()
    }
  }

  /**
   * Handle Escape key
   * Calls the escape callback if defined
   */
  function eventKeyupEscape (e: KeyboardEvent) {
    if (e.key === 'Escape') {
      focus.escapeCallback?.()
    }
  }

  function eventKeydownTab (e: KeyboardEvent) {
    if (e.type === 'keydown' && e.key === 'Tab') {
      e.preventDefault()
      focus.tabCallback?.()
    }
  }

  function handleSliderNavigation (visibleItems: HTMLElement[], index: number) {
    const flickity = flickityRef?.value
    if (!flickity) return
    flickity.selectCell(index)
    visibleItems[index]?.focus()
  }

  function eventKeydownArrow (e: KeyboardEvent) {
    const keys = ['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight', 'Home', 'End']
    if (!keys.includes(e.key)) return

    const flickity = flickityRef?.value
    const visibleItems = focus.items.filter(el => el.offsetParent !== null)
    const currentIndex = visibleItems.indexOf(document.activeElement as HTMLElement)

    if (e.key === 'ArrowDown' && currentIndex < visibleItems.length - 1 && !flickity) {
      e.preventDefault()
      visibleItems[currentIndex + 1].focus()
    }
    if (e.key === 'ArrowUp' && currentIndex > 0 && !flickity) {
      e.preventDefault()
      visibleItems[currentIndex - 1].focus()
    }
    if (e.key === 'ArrowLeft' && flickity) {
      e.preventDefault()
      handleSliderNavigation(visibleItems, currentIndex > 0 ? currentIndex - 1 : visibleItems.length - 1)
    }
    if (e.key === 'ArrowRight' && flickity) {
      e.preventDefault()
      handleSliderNavigation(visibleItems, currentIndex < visibleItems.length - 1 ? currentIndex + 1 : 0)
    }
    if (e.key === 'Home') {
      e.preventDefault()
      flickity?.selectCell(0)
      visibleItems[0]?.focus()
    }
    if (e.key === 'End') {
      e.preventDefault()
      flickity?.selectCell(visibleItems.length - 1)
      visibleItems[visibleItems.length - 1]?.focus()
    }
  }

  function getNextFocusableElement (el: HTMLElement): HTMLElement | null {
    const focusableSelector = [
      'a[href]',
      'area[href]',
      'input:not([disabled]):not([tabindex="-1"])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      'button:not([disabled])',
      'iframe',
      'object',
      'embed',
      '[tabindex]:not([tabindex="-1"])',
      '[contenteditable]'
    ].join(',')

    const all = Array.from(document.querySelectorAll<HTMLElement>(focusableSelector))
      .filter(el => el.offsetWidth > 0 || el.offsetHeight > 0 || el.getClientRects().length > 0)

    const currentIndex = all.indexOf(el)
    return all[currentIndex + 1] ?? null
  }

  return {
    leaveFocusOnTab,
    checkKeysFirstLast,
    keepTabbingFocus,
    addTabbingEventListeners,
    removeTabbingEventListeners,
    eventKeydownFirst,
    eventKeydownLast,
    eventKeyupEscape,
    eventKeydownTab,
    handleSliderNavigation,
    eventKeydownArrow,
    getNextFocusableElement
  }
}
